import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Brain, HeartHandshake, Moon, Sparkles } from "lucide-react";
import PageLayout from "@/components/PageLayout";
import Hero from "@/components/Hero";
import SEO from "@/components/SEO";
import { SERVICES } from "@/lib/site";

const iconMap = { brain: Brain, "heart-handshake": HeartHandshake, moon: Moon, sparkles: Sparkles };

export default function Home() {
  return (
    <PageLayout>
      <SEO
        title="Psicologa a Chivasso (Torino)"
        description="Dott.ssa Chiara Maiolo, psicologa clinica e sessuologa clinica a Chivasso (Torino). Esperta in comunicazione ipnotica. Sedute in studio e online. Prenota un primo colloquio senza giudizio."
        keywords="psicologa Chivasso, psicologa Torino, sessuologa clinica, comunicazione ipnotica, terapia online, Chiara Maiolo"
        path="/"
      />
      <div data-testid="home-page">
        <Hero />

        {/* Services preview */}
        <section className="py-20 lg:py-28 bg-[#E9EFE4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <div className="max-w-xl">
                <span className="eyebrow">I miei servizi</span>
                <h2 className="font-serif-elegant mt-5 text-3xl lg:text-4xl font-light text-[#3D3B3A] leading-tight">
                  Tre percorsi,<br />
                  <span className="italic text-[#6B8568]">un&apos;unica visione</span>.
                </h2>
              </div>
              <Link to="/servizi" className="text-sm text-[#3D3B3A] hover:text-[#7E9C7B] border-b border-[#3D3B3A] pb-1" data-testid="home-services-link">
                Scopri tutti i servizi →
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {SERVICES.map((s, i) => {
                const Icon = iconMap[s.icon];
                return (
                  <motion.div
                    key={s.key}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                  >
                    <Link
                      to="/servizi"
                      data-testid={`home-service-${s.key}`}
                      className="block bg-[#F6F8F3] border border-[#CFD9C5] hover:border-[#9DB39A] rounded-2xl p-7 h-full transition-all duration-300 group"
                    >
                      <Icon size={28} strokeWidth={1} className="text-[#9DB39A] mb-5" />
                      <h3 className="font-serif-elegant text-xl text-[#3D3B3A] group-hover:text-[#7E9C7B] transition-colors">
                        {s.title}
                      </h3>
                      <p className="mt-3 text-sm text-[#73706E] leading-relaxed line-clamp-3">{s.desc}</p>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* About preview */}
        <section className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="eyebrow">Chi sono</span>
              <h2 className="font-serif-elegant mt-5 text-3xl lg:text-4xl font-light text-[#3D3B3A] leading-tight">
                Ti accolgo nel mio studio<br />
                <span className="italic text-[#6B8568]">a Chivasso</span>.
              </h2>
              <p className="mt-6 text-[#73706E] leading-relaxed">
                Sono Chiara Maiolo, psicologa clinica e sessuologa clinica,
                esperta in comunicazione ipnotica. Lavoro con apertura
                e senza giudizio accanto a chi cerca uno spazio per ascoltarsi
                e affrontare le difficoltà del quotidiano.
              </p>
              <Link to="/chi-sono" className="mt-8 inline-flex items-center gap-2 text-sm text-[#3D3B3A] hover:text-[#7E9C7B] border-b border-[#3D3B3A] pb-1" data-testid="home-about-link">
                La mia storia <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-[#E9EFE4] max-w-md ml-auto w-full">
              <img
                src="https://images.pexels.com/photos/4101143/pexels-photo-4101143.jpeg?auto=compress&cs=tinysrgb&w=900"
                alt="Studio di psicologia · Dott.ssa Chiara Maiolo"
                className="w-full h-full object-cover"
                data-testid="home-studio-photo"
              />
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section className="px-6 lg:px-12 pb-20 lg:pb-28">
          <div className="max-w-7xl mx-auto bg-[#3D3B3A] text-white rounded-[2.5rem] px-8 py-16 lg:p-20 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: "url(https://images.pexels.com/photos/7605642/pexels-photo-7605642.jpeg?auto=compress&cs=tinysrgb&w=1200)",
              backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "screen"
            }} />
            <div className="relative">
              <span className="eyebrow text-white/60" style={{ color: "rgba(255,255,255,0.6)" }}>
                Prenotazione online
              </span>
              <h2 className="font-serif-elegant mt-6 text-4xl lg:text-5xl font-light leading-tight">
                Il primo passo è<br />
                <span className="italic text-[#9DB39A]">scegliere uno spazio per sé</span>.
              </h2>
              <p className="mt-6 max-w-xl mx-auto text-white/70 leading-relaxed">
                Prenota un primo colloquio direttamente online. Sceglierai data,
                orario e tipologia di percorso. Ti contatterò per la conferma.
              </p>
              <Link
                to="/prenota"
                data-testid="home-cta-prenota"
                className="mt-10 inline-flex items-center gap-2 bg-[#9DB39A] text-white hover:bg-[#7E9C7B] rounded-full px-10 py-4 text-sm tracking-wide transition-all"
              >
                Prenota una seduta <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}
